import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useTheme,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
// Custom components
import Card from "components/card/Card";
import { Pagination } from "components/customs/pagination";
import { CustomNoDataFound } from "components/customs/noDataFound";
import { BackButton } from "components/customs/backButton";
import { PrimayButton } from "components/customs/buttons";
import { setHasNewNotifications } from "store/slices/notifications";
import { useDispatch } from "react-redux";
import { readSingleNotificationThunk } from "store/slices/notifications";
import { useAuthSelector } from "store/slices/auth";
import { useNotificationsCountSelector } from "store/slices/notifications";

export function NotificationsTable(props) {
  const dispatch = useDispatch();
  const notificationsCount = useNotificationsCountSelector();
  const auth = useAuthSelector();
  const userId = auth?.user?.data?.user?.id;

  const {
    loading,
    columnsData,
    tableData,
    totalPages,
    currentPage,
    setCurrentPage,
    notificationType,
    handleNotificationType,
    handleMarkAllAsRead,
  } = props;

  const tableInstance = useTable(
    {
      columns: columnsData,
      data: tableData,
      manualPagination: true, // Use manual pagination to handle API
      pageCount: totalPages,
      state: { pageIndex: currentPage },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
  } = tableInstance;
  const theme = useTheme();
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const tabBorderColor = useColorModeValue("navy.700", "white");
  const actualTabBorderColor = theme.colors.navy?.[700] || tabBorderColor;
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const unreadRowBg = useColorModeValue("rgba(23,44,135,0.10)", "whiteAlpha.100");
  const unreadRowHoverBg = useColorModeValue("rgba(23,44,135,0.16)", "whiteAlpha.200");

  useEffect(() => {
    dispatch(setHasNewNotifications(false));
  }, []);

  function handleNext() {
    if (canNextPage) {
      setCurrentPage((prev) => prev + 1);
      nextPage();
    }
  }

  function handlePrevious() {
    if (!canPreviousPage) {
      setCurrentPage((prev) => prev - 1);
      previousPage();
    }
  }

  function handleRowClick({ notificationId,  page, notificationType }) {
    dispatch(readSingleNotificationThunk({ notificationId,  page, notificationType }));
  }

  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      // overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Flex
        px="25px"
        gap={5}
        mb="5"
        align="start"
        justifyContent="space-between"
      >
        <Flex px="25px" gap={5} mb="5" align="start">
          <Text
            textTransform="capitalize"
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
            cursor="pointer"
            onClick={() => handleNotificationType({ notificationType: "all" })}
            borderBottom={
              notificationType === "all"
                ? `4px solid ${actualTabBorderColor}`
                : "none"
            }
          >
            All
          </Text>
          <Text
            textTransform="capitalize"
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
            cursor="pointer"
            onClick={() => handleNotificationType({ notificationType: "read" })}
            borderBottom={
              notificationType === "read"
                ? `4px solid ${actualTabBorderColor}`
                : "none"
            }
          >
            Read
          </Text>
          <Text
            textTransform="capitalize"
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
            cursor="pointer"
            onClick={() =>
              handleNotificationType({ notificationType: "unread" })
            }
            borderBottom={
              notificationType === "unread"
                ? `4px solid ${actualTabBorderColor}`
                : "none"
            }
          >
            Unread
            {notificationsCount > 0 ? (
              <Box
                as="span"
                display="inline-block"
                ml="8px"
                w="8px"
                h="8px"
                borderRadius="full"
                bg="var(--chakra-colors-navy-700)"
                verticalAlign="middle"
              />
            ) : null}
          </Text>
        </Flex>
        <PrimayButton
          label="Mark All As Read"
          onClick={handleMarkAllAsRead}
          disabled={loading || notificationsCount <= 0}
        />
      </Flex>

      <TableContainer>
        <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
          <Thead>
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    pe="10px"
                    key={index}
                    borderColor={borderColor}
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="gray.400"
                      // fontWeight="800"
                    >
                      {column.render("Header")}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              const isRead = row.original.readBy.includes(userId)
              return (
                <Tr
                  {...row.getRowProps()}
                  key={index}
                  cursor={"pointer"}
                  // cursor={isRead ? "auto" : "pointer"}
                  borderBottom="1.5px solid #172C87"
                  bg={isRead ? "transparent" : unreadRowBg}
                  _hover={{
                    bg: isRead ? "transparent" : unreadRowHoverBg,
                  }}
                  onClick={() => {
                    if (isRead) return;
                    handleRowClick({ notificationId: row.original.id, notificationType,  page:currentPage, })
                  }}
                >
                  {row.cells.map((cell, index) => {
                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        minW={{ sm: "150px", md: "200px", lg: "auto" }}
                        borderColor="transparent"
                      >
                        <Flex align="center" padding="6px">
                          {!isRead && cell.column.id === "message" ? (
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="full"
                              bg="var(--chakra-colors-navy-700)"
                              mr="8px"
                              flexShrink={0}
                            />
                          ) : null}
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="500"
                          >
                            {cell.value}
                          </Text>
                        </Flex>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        {totalPages === 0 ? (
          <CustomNoDataFound />
        ) : (
          <Pagination
            {...{
              currentPage,
              totalPages,
              handleNext,
              handlePrevious,
            }}
          />
        )}
      </TableContainer>
    </Card>
  );
}
